import PropTypes from 'prop-types';
import { complement, pathEq, map, groupBy, prop as rProp, pipe } from 'ramda';
import React, { useState, useEffect, useCallback, useRef } from 'react';

import { Icon } from '@ui/icons/Icon.tsx';

import { useCSSColors } from '../../../../src/hooks/useCSSColors.ts';
import BuildingLevelSelectorDesktop from './BuildingLevelSelectorDesktop.tsx';
import { Wrapper, ToggleShowMenuButtonMobile, OtherResultsIndicatorMobile } from './styles.ts';

const MapLevelSelectorOnline = ({ widgetState, bus, T, isMobile }) => {
  const colors = useCSSColors();
  const [state, setState] = useState(widgetState.getState());

  useEffect(() => widgetState.addCallback(setState), [widgetState]);

  useEffect(() => setState(widgetState.getState()), []);

  const onBuildingClick = buildingId => {
    bus.send('mapLevelSelector/selectBuilding', { id: buildingId, userInitiated: true });
  };

  const onLevelClick = levelId => {
    bus.send('mapLevelSelector/selectLevel', {
      id: levelId,
      centerMap: true,
      userInitiated: true,
    });
  };

  const areResultsOnOtherFloorsOrBuildings = useCallback(({ selectedBuilding, selectedLevel, searchResults }) => {
    if (!(selectedLevel || selectedBuilding)) {
      return Boolean(searchResults && searchResults.length);
    }
    const anyDifferent = (path, value) => searchResults.some(complement(pathEq(value, path)));
    if (selectedLevel) {
      return anyDifferent(['position', 'floorId'], selectedLevel.id);
    }
    if (selectedBuilding) {
      return anyDifferent(['position', 'buildingId'], selectedBuilding.id);
    }
  });

  const getBuildingsSearchResults = pipe(map(rProp('position')), groupBy(rProp('buildingId')));

  const { isMenuOpen, buildings, selectedBuilding, selectedLevel } = state;

  const buildingsSearchResults = getBuildingsSearchResults(state.searchResults);
  const getLevelsSearchResults = groupBy(rProp('floorId'));

  const getLevelsWithSearchResults = (levels, buildingSearchResults) => {
    const levelsSearchResults = getLevelsSearchResults(buildingSearchResults);

    return levels.map(level => {
      const levelSearchResults = levelsSearchResults[level.id];
      if (!levelSearchResults) {
        return level;
      }

      return { ...level, resultsNumber: levelSearchResults.length };
    });
  };
  const buildingsWithSearchResults =
    buildings &&
    buildings.map(building => {
      const buildingSearchResults = buildingsSearchResults[building.id];
      if (!buildingSearchResults) {
        return building;
      }
      return {
        ...building,
        resultsNumber: buildingSearchResults.length,
        levels: getLevelsWithSearchResults(building.levels, buildingSearchResults),
      };
    });

  const desktopLevelSelectorRef = useRef(null);

  return (
    <Wrapper className={isMenuOpen ? 'open' : 'closed'} aria-label={T('fullscreenOnlyLayout:Level selector dropdown')}>
      {isMobile ? (
        <ToggleShowMenuButtonMobile
          onClick={() => {
            if (isMenuOpen) {
              bus.send('mapLevelSelectorOnline/closeLevelSelector');
            } else {
              bus.send('mapLevelSelectorOnline/openLevelSelector');
            }
          }}
          aria-expanded={isMenuOpen}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              if (isMenuOpen) {
                bus.send('mapLevelSelectorOnline/closeLevelSelector');
              } else {
                bus.send('mapLevelSelectorOnline/openLevelSelector');
              }
            }
          }}
          title="Toggle Level Selector Menu"
          aria-label={[
            T('mapLevelSelectorOnline:Toggle Level Selector Menu'),
            selectedBuilding && selectedBuilding.name,
            selectedLevel && selectedLevel.name,
          ]
            .filter(Boolean)
            .join(', ')}
          data-testid="MapLevelSelectorMobileButton"
        >
          {areResultsOnOtherFloorsOrBuildings(state) ? (
            <OtherResultsIndicatorMobile data-testid="otherResultsIndicator" />
          ) : null}
          <Icon name="levels" cdn width={32} height={32} fillColor={colors.primaryText} />
        </ToggleShowMenuButtonMobile>
      ) : (
        <BuildingLevelSelectorDesktop
          ref={desktopLevelSelectorRef}
          data-testid="MapLevelSelector"
          onBuildingClick={onBuildingClick}
          onLevelClick={onLevelClick}
          fallbackLevelName={T('mapLevelSelectorOnline:ellipses')}
          fallbackBuildingName={T('mapLevelSelectorOnline:Select a Building') + '…'}
          selectedBuildingId={selectedBuilding && selectedBuilding.id}
          currentBuildingName={selectedBuilding && selectedBuilding.name}
          selectedLevelId={selectedLevel && selectedLevel.id}
          currentLevelName={selectedLevel && selectedLevel.name}
          tooltipContent={T('mapLevelSelectorOnline:Show Buildings & Levels')}
          buildings={buildingsWithSearchResults || buildings}
          isExpanded={isMenuOpen}
          onTriggerClick={() => {
            if (isMenuOpen) {
              bus.send('mapLevelSelectorOnline/closeLevelSelector');
            } else {
              bus.send('mapLevelSelectorOnline/openLevelSelector');
            }
          }}
          withSearchResults={areResultsOnOtherFloorsOrBuildings(state)}
          T={T}
        />
      )}
    </Wrapper>
  );
};

MapLevelSelectorOnline.propTypes = {
  bus: PropTypes.shape({
    on: PropTypes.func.isRequired,
    send: PropTypes.func.isRequired,
  }),
};

export default MapLevelSelectorOnline;
